import { Component, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { BaryModalEvent, IBaryModalEvent } from '../../../../../../models/bary-modal-event';
import { BaryComponentHelper } from '../../../../util/bary-component-helper';
import { MDBModalRef, MDBModalService } from 'ng-uikit-pro-standard';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BasketOrderExtendedService } from '../../../../../../services/basket-order-extended.service';
import { TranslateService } from '@ngx-translate/core';
import { ReasonRefund } from '../../../../../../models/enums/reason-refund.enum';

import { TransportOrderModificationService } from '../../../../../../services/transport-order-modification.service';
import { TransportOrderModificationCauseService } from '../../../../../../services/transport-order-modification-cause.service';
import { forkJoin } from 'rxjs';
import { CartGroupValueItem } from '../../../../../../models/cart-group-value-item.model';
import { BaryValidator } from '../../../../../../validators/bary.validator';
import { BasketOrderView } from '../../../../../../models/basket-order-view.model';
import { TransportOrderRefundView } from '../../../../../../models/transport-order-refund-view.model';
import { TransportOrderModificationCause } from '../../../../../../models/transport-order-modification-cause.model';
import { ModificationTypeEnum, TransportOrderModification } from '../../../../../../models/transport-order-modification.model';
import { PaymentMethod } from '../../../../../../models/payment-method.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { BaryModalErrorRefundPayzenComponent } from '../bary-modal-error-refund-payzen/bary-modal-error-refund-payzen.component';
import { FaDirectiveEnum } from '../../../../../../models/enums/fa-directive.enum';
import { CalculationMethodEnum } from '../../../../../../models/enums/calculation-method.enum';

@Component({
  selector: 'jhi-bary-modal-refund',
  templateUrl: './bary-modal-refund.component.html',
  styleUrls: ['./bary-modal-refund.component.scss'],
})
export class BaryModalRefundComponent implements OnInit {
  readonly #modalRef = inject(MDBModalRef);
  readonly #basketOrderService = inject(BasketOrderExtendedService);
  readonly #transportOrderModificationService = inject(TransportOrderModificationService);
  readonly #transportOrderModificationCauseService = inject(TransportOrderModificationCauseService);
  readonly #translateService = inject(TranslateService);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #mdbModalService = inject(MDBModalService);

  public action = BaryComponentHelper.generateAction<IBaryModalEvent>();
  public basketOrder!: BasketOrderView;
  public forms: FormGroup = new FormGroup({});
  public transportOrderReviewRefundListFilter: TransportOrderRefundView[] = [];
  public transportOrderReviewRefundListInitial: TransportOrderRefundView[] = [];
  public causes: TransportOrderModificationCause[] = [];

  public acceptAllRefunds: FormControl = new FormControl(false);
  public searchTerm: FormControl = new FormControl('');

  public option: { label: string; value: any }[] = [];

  protected readonly FAS = FaDirectiveEnum.FAS;

  @Output() closeEvent = new EventEmitter();

  @ViewChild('refundTable') refundTable!: ElementRef;

  public ngOnInit(): void {
    this.#spinnerService.show().then();
    forkJoin({
      tors: this.#basketOrderService.getBasketOrderRefunds(this.basketOrder.id!),
      causes: this.#transportOrderModificationCauseService.getByModificationType(ModificationTypeEnum.REFUND),
    })
      .subscribe({
        next: result => {
          this.causes = result.causes;
          this.initOption();
          this.transportOrderReviewRefundListInitial = result.tors;
          this.searchTerm.valueChanges.subscribe({
            next: (val: string) => this.initForm(val),
          });
          this.initForm();
        },
      })
      .add((): void => {
        this.#spinnerService.hide().then();
      });
  }

  private initForm(searchTerm?: string): void {
    this.transportOrderReviewRefundListFilter = this.transportOrderReviewRefundListInitial
      .filter(val => !searchTerm || `OT${val.id}`.includes(searchTerm))
      .map((transportOrder: TransportOrderRefundView) => {
        const lineFormGroup = new FormGroup(
          {
            refundAmount: new FormControl(transportOrder?.form?.value?.refundAmount || 0, [Validators.min(0), Validators.max(transportOrder.transportRefundMaxAmountHT)]),
            reason: new FormControl(transportOrder?.form?.value?.reason || ReasonRefund.EMPTY_REASON),
            contractor: new FormControl(transportOrder?.form?.value?.contractor || ''),
            transportOrderId: new FormControl(transportOrder.id),
          },
          { updateOn: 'change' }
        );
        lineFormGroup.setValidators(BaryValidator.validatorRefundRequired());
        this.forms.addControl(`form${transportOrder.id}`, lineFormGroup);
        Object.assign(transportOrder, { form: lineFormGroup });
        return transportOrder;
      });
    this.forms.markAllAsTouched();
  }

  public getOldTotalRefund(): number {
    return this.transportOrderReviewRefundListFilter
      .filter(val => !!val)
      .map((t: TransportOrderRefundView) => t?.transportRefundAmountTTC || 0)
      .reduce((a: number, b: number) => a + b, 0);
  }

  public getAllRefundSaisie(): number {
    return this.transportOrderReviewRefundListFilter
      .filter(val => !!val)
      .map(val => (val?.form?.value?.refundAmount || 0) as number)
      .reduce((a: number, b: number) => a + b, 0);
  }

  public handleClick(): void {
    this.#modalRef.hide();
  }

  public handleSubmit(): void {
    this.#spinnerService.show().then();
    const result = this.transportOrderReviewRefundListFilter
      .filter(val => val.form.value.refundAmount > 0)
      .map(
        val =>
          ({
            modificationType: ModificationTypeEnum.REFUND,
            contractor: val.form.value.contractor,
            createdDate: new Date(),
            date: new Date(),
            calculationMethod: CalculationMethodEnum.ADD_FIXED,
            value: val.form.value.refundAmount,
            amount: val.form.value.refundAmount,
            transportOrderId: val.id,
            cause: val.form.value.reason as TransportOrderModificationCause,
          } as TransportOrderModification)
      );
    this.#transportOrderModificationService.saveAll(this.basketOrder.id!, result).subscribe({
      next: (): void => {
        this.action.next(new BaryModalEvent({ first: true }));
        this.#modalRef.hide();
        this.#spinnerService.hide().then();
      },
      error: (e: any): void => {
        this.#spinnerService.hide().then();
        this.#modalRef.hide();
        this.#mdbModalService.show(BaryModalErrorRefundPayzenComponent, {
          data: {
            errorMessage: e?.baryError?.errorParams['errorMessage'],
            errorCode: e?.baryError?.errorParams['errorCode'],
          },
          animated: true,
          ignoreBackdropClick: true,
          class: 'modal-lg',
        });
      },
    });
  }

  private initOption(): void {
    this.option = [
      {
        label: this.#translateService.instant('MODAL_REFUND_BILL.EMPTY_REASON'),
        value: ReasonRefund.EMPTY_REASON,
      },
    ];
    this.causes.forEach(val => this.option.push({ label: val.value, value: val }));
  }

  public getBillHTItems(): CartGroupValueItem[] {
    const items: CartGroupValueItem[] = [];

    items.push(
      new CartGroupValueItem({
        value: this.basketOrder.gazoleTax!,
        label: this.#translateService.instant('MODAL_REFUND_BILL.SUB_BLOCK.WITH_GAZOLE_TAX', { gazoleTaxRate: this.basketOrder.gazoleTaxRate }),
      })
    );
    if (this.basketOrder.insurance) {
      items.push(
        new CartGroupValueItem({
          value: this.basketOrder.insurance,
          label: this.#translateService.instant('MODAL_REFUND_BILL.SUB_BLOCK.WITH_INSURANCE'),
        })
      );
    }

    return items;
  }

  public getRefundHt(): number {
    return Math.min(this.getAllRefundSaisie(), this.basketOrder.priceHt!);
  }

  public getRefundTTC(): number {
    return Math.min(this.getRefundHt() + this.calculateTaxes(), this.basketOrder.price!);
  }

  public getRefundVat(): number {
    // Si c'est le montant du prix du transport alors on prend celui indiqué dans le basketOrder
    return Math.min(this.calculateTaxes(), this.basketOrder.vat!);
  }

  public calculateTaxes(): number {
    return this.getAllRefundSaisie() * 0.2;
  }

  public getPaymentMethod(): string {
    if (this.basketOrder.paymentMethod instanceof PaymentMethod) {
      return this.#translateService.instant(this.basketOrder.paymentMethod.translationKey);
    }
    return this.basketOrder.paymentMethod;
  }
}
